// i18n
import { translate, translateInComponent } from 'infras/i18n';
import translations from '../i18n.json';

import { useState, useContext, useEffect, SyntheticEvent } from 'react';
import { apiPath } from 'presentations/routes/path';
import {
  DashboardPortalContext,
  setErrorMessage,
  setProjects,
} from 'presentations/reducers/DashboardPortal';
import { post, patch, RestClientResponse } from 'infras/rest_client';
import { defaultIcon } from 'domains/models/project';
import {
  ProjectCreateBody,
  ProjectUpdateBody,
  ProjectItem,
  ProjectListItem,
} from 'presentations/api/schema/project';
import { DisplayType } from 'domains/models/project/displayType';
import { getErrorMessage } from 'presentations/views/helpers/error';

interface Props {
  teamUid?: string;
  project?: ProjectItem | ProjectListItem;
  onRequested?: (project: ProjectItem | ProjectListItem) => void;
}
export function useProjectForm(props: Props) {
  const t = translateInComponent(translations);
  const { state, dispatch } = useContext(DashboardPortalContext);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [name, setName] = useState<string | null>(props.project?.name || null);
  const [displayType, setDisplayType] = useState<DisplayType>(
    props.project?.displayType || DisplayType.TeamPublic,
  );
  const [isDefaultAdd, setIsDefaultAdd] = useState<boolean>(
    Boolean(props.project?.isDefaultAdd || false),
  );
  const [icon, setIcon] = useState<string | null>(
    props.project?.icon || defaultIcon(),
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (props.project) {
      setName(props.project.name);
      setIcon(props.project.icon);
      setIsDefaultAdd(props.project.isDefaultAdd);
    }
  }, [props.project]);

  const onCreatedProject = (project: ProjectListItem) => {
    dispatch(setProjects([...state.projects, project]));
    props.onRequested && props.onRequested(project);
  };

  const onUpdatedProject = () => {
    if (!props.project) {
      return;
    }
    const project: ProjectItem = {
      ...props.project,
      name: name || props.project.name,
      icon: icon || props.project.icon,
      isDefaultAdd: isDefaultAdd || props.project.isDefaultAdd,
      displayType,
      users: 'users' in props.project ? props.project.users : [],
    };

    dispatch(
      setProjects(
        state.projects.map((p) => (p.uid === project.uid ? project : p)),
      ),
    );
    props.onRequested && props.onRequested(project);
  };

  const onChange = (e: SyntheticEvent<HTMLInputElement>) => {
    const name = e.currentTarget.name;
    const value = e.currentTarget.value;
    switch (name) {
      case 'name':
        setName(value);
        break;
      case 'isDefaultAdd':
        setIsDefaultAdd(!isDefaultAdd);
        break;
      case 'displayType':
        const d = Number(value);
        setDisplayType(d);
        if (d === DisplayType.Private) {
          setIsDefaultAdd(false);
        }
        break;
      default:
        break;
    }
  };

  const onChangeIcon = (id: string) => {
    setIcon(id);
  };

  const onSubmit = async () => {
    // TODO validation and error handling
    if (!props.teamUid) return;
    if (!name) return;
    if (!icon) return;

    setIsLoading(true);
    let result: RestClientResponse;
    if (props.project) {
      const data: ProjectUpdateBody = {
        name,
        icon,
        isDefaultAdd,
        displayType,
      };
      result = await patch(
        apiPath(
          `/v1/teams/${props.teamUid}/projects/${props.project.uid}/update`,
        ),
        { data },
      );
    } else {
      const data: ProjectCreateBody = {
        name,
        icon,
        isDefaultAdd,
        displayType,
      };
      result = await post(
        apiPath(`/v1/teams/${props.teamUid}/projects/create`),
        { data },
      );
    }
    if (!result.isSuccess) {
      setIsLoading(false);
      dispatch(setErrorMessage(translate(getErrorMessage(result))));
      return;
    }
    setIsLoading(false);
    if (props.project) {
      onUpdatedProject();
    } else {
      onCreatedProject(result.data);
      setIcon(null);
      setName(null);
    }
    setIsOpen(false);
  };

  const onCancel = () => {
    setIsOpen(false);
  };

  return {
    state: {
      isOpen,
      name,
      icon,
      isDefaultAdd,
      displayType,
      isLoading,
    },
    actions: {
      setIsOpen,
      onChange,
      onChangeIcon,
      onCancel,
      onSubmit,
    },
  };
}
